import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, MapPin, Camera, Copy, Share2, CheckCircle2, Building2, Factory,
  Briefcase, Hotel, Home as HomeIcon, Dumbbell, Truck, MoreHorizontal, Locate,
} from "lucide-react";

const STEPS = ["Location", "Type", "Details", "Photos", "Manager", "Verification", "Review"];
const LOCATION_TYPES = [
  { key: "warehouse", label: "Warehouse", icon: Building2 },
  { key: "manufacturing", label: "Manufacturing", icon: Factory },
  { key: "office", label: "Office", icon: Briefcase },
  { key: "hotel", label: "Hotel", icon: Hotel },
  { key: "apartment", label: "Apartment", icon: HomeIcon },
  { key: "gym", label: "Gym", icon: Dumbbell },
  { key: "logistics", label: "Trucking/Logistics", icon: Truck },
  { key: "other", label: "Other", icon: MoreHorizontal },
];
const MACHINE_TYPES = ["Snack", "Drink", "Combo", "Smart Cooler", "Coffee"];
const EMP_RANGES = ["< 25", "25–50", "50–100", "100–250", "250–500", "500+"];

export default function SubmitSpot() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    business: "Meridian Logistics", address: "1501 Centerpoint Dr, Joliet, IL 60436",
    type: "warehouse", people: "100–250", open247: true, openTime: "07:00", closeTime: "19:00",
    shifts: "3", current: "no", desired: ["Snack", "Drink"], photos: { exterior: true, area: true, extra: false },
    manager: "John Smith", title: "Operations Manager", phone: "(815) 555-0142", email: "j.smith@meridianlogistics.com",
  });
  const [copied, setCopied] = useState(false);
  const verifyUrl = "spotbounty.com/verify/spb-9482-XKR";

  const next = () => step < STEPS.length - 1 && setStep(step + 1);
  const back = () => step > 0 ? setStep(step - 1) : nav("/app/scout");
  const submit = () => { toast.success("Spot submitted for verification."); nav("/app/scout"); };

  const setField = (k, v) => setD({ ...d, [k]: v });
  const toggleDesired = (v) => setField("desired", d.desired.includes(v) ? d.desired.filter((x) => x !== v) : [...d.desired, v]);
  const copyLink = () => {
    navigator.clipboard?.writeText(`https://${verifyUrl}`).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    toast.success("Verification link copied.");
  };

  return (
    <div className="p-4 pb-6" data-testid="submit-spot-wizard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={back} className="w-8 h-8 rounded-full bg-[#15191D] border border-[#2A2F34] flex items-center justify-center">
          <ArrowLeft size={14} />
        </button>
        <div className="text-[11px] text-[#A1A1AA]">Step {step + 1} of {STEPS.length} · {STEPS[step]}</div>
        <div className="w-8" />
      </div>

      {/* Progress */}
      <div className="mt-3">
        <div className="sb-progress-track">
          <div className="sb-progress-fill" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <div className="mt-2 flex justify-between">
          {STEPS.map((_, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i <= step ? "bg-[#FFC107]" : "bg-[#2A2F34]"}`} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mt-6">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-[22px] font-black">Where's the spot?</h2>
            <div><label className="sb-label">Business Name</label><input className="sb-input" value={d.business} onChange={(e) => setField("business", e.target.value)} /></div>
            <div><label className="sb-label">Business Address</label><input className="sb-input" value={d.address} onChange={(e) => setField("address", e.target.value)} /></div>
            <button className="sb-btn-secondary w-full border-[#FFC107]/40 text-[#FFC107]" type="button">
              <Locate size={14} /> Use Current GPS Location
            </button>
            <div className="sb-map h-40 relative">
              <div className="sb-pin" style={{ left: "50%", top: "50%" }} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="text-[22px] font-black">What type of location?</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {LOCATION_TYPES.map((t) => {
                const Icon = t.icon; const active = d.type === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setField("type", t.key)}
                    data-testid={`type-${t.key}`}
                    className={`sb-card p-4 text-left transition-all ${active ? "border-[#FFC107] bg-[#FFC107]/5" : ""}`}
                  >
                    <Icon size={20} className={active ? "text-[#FFC107]" : "text-white"} />
                    <div className="mt-2 text-[13px] font-bold">{t.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-[22px] font-black">Details</h2>
            <div>
              <label className="sb-label">Estimated People</label>
              <select className="sb-input" value={d.people} onChange={(e) => setField("people", e.target.value)}>
                {EMP_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="sb-label">Hours</label>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={d.open247} onChange={(e) => setField("open247", e.target.checked)} /> Open 24/7
              </label>
              {!d.open247 && (
                <div className="flex gap-2 mt-2">
                  <input type="time" className="sb-input" value={d.openTime} onChange={(e) => setField("openTime", e.target.value)} />
                  <input type="time" className="sb-input" value={d.closeTime} onChange={(e) => setField("closeTime", e.target.value)} />
                </div>
              )}
            </div>
            <div>
              <label className="sb-label">Number of Shifts</label>
              <div className="flex gap-2">
                {["1", "2", "3"].map((n) => (
                  <button key={n} onClick={() => setField("shifts", n)} className={`sb-pill ${d.shifts === n ? "sb-pill-gold" : "sb-pill-gray"}`}>{n}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="sb-label">Current Vending?</label>
              <div className="flex gap-2">
                {[["yes", "Yes"], ["no", "No"]].map(([k, l]) => (
                  <button key={k} onClick={() => setField("current", k)} className={`sb-pill ${d.current === k ? "sb-pill-gold" : "sb-pill-gray"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="sb-label">Desired Vending Type</label>
              <div className="flex flex-wrap gap-2">
                {MACHINE_TYPES.map((m) => (
                  <button key={m} onClick={() => toggleDesired(m)} className={`sb-pill ${d.desired.includes(m) ? "sb-pill-gold" : "sb-pill-gray"}`}>{m}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-[22px] font-black">Photos</h2>
            <p className="mt-1 text-[12px] text-[#A1A1AA]">Add a few photos to help operators evaluate.</p>
            <div className="mt-4 grid gap-3">
              {[
                { key: "exterior", label: "Exterior Photo" },
                { key: "area", label: "Proposed Vending Area" },
                { key: "extra", label: "Additional Photos" },
              ].map((p) => (
                <div key={p.key} className="sb-card p-3 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#0E1215] border border-[#2A2F34] flex items-center justify-center shrink-0 relative">
                    {d.photos[p.key] ? (
                      <img src="https://images.unsplash.com/photo-1553413077-190dd305871c?w=200&q=60" alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Camera size={20} className="text-[#52525b]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-[13px] font-bold">{p.label}</div>
                    <div className="text-[11px] text-[#A1A1AA]">{d.photos[p.key] ? "1 photo added" : "Tap to upload"}</div>
                  </div>
                  <button onClick={() => setField("photos", { ...d.photos, [p.key]: !d.photos[p.key] })} className="sb-pill sb-pill-gray">
                    {d.photos[p.key] ? "Remove" : "Add"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-[22px] font-black">Decision Maker</h2>
            <div><label className="sb-label">Manager Name</label><input className="sb-input" value={d.manager} onChange={(e) => setField("manager", e.target.value)} /></div>
            <div><label className="sb-label">Title</label><input className="sb-input" value={d.title} onChange={(e) => setField("title", e.target.value)} /></div>
            <div><label className="sb-label">Phone</label><input className="sb-input" value={d.phone} onChange={(e) => setField("phone", e.target.value)} /></div>
            <div><label className="sb-label">Email</label><input className="sb-input" value={d.email} onChange={(e) => setField("email", e.target.value)} /></div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="text-[22px] font-black">Business Verification</h2>
            <p className="mt-1 text-[12px] text-[#A1A1AA]">Share this link with the manager. They'll confirm interest.</p>
            <div className="mt-4 sb-card p-4">
              <div className="sb-eyebrow" style={{ fontSize: 10 }}>Verification Link</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="sb-mono text-[12px] flex-1 truncate">{verifyUrl}</div>
                <button onClick={copyLink} className="sb-pill sb-pill-gray"><Copy size={11} /> {copied ? "Copied" : "Copy"}</button>
              </div>
              <div className="mt-3 flex gap-2">
                <button className="sb-btn-secondary flex-1 text-[12px]"><Share2 size={13} /> Share Link</button>
              </div>
            </div>
            <div className="mt-4 sb-card p-4 flex flex-col items-center">
              <div className="p-3 bg-white rounded-lg">
                <QRCode value={`https://${verifyUrl}`} size={140} bgColor="#ffffff" fgColor="#080B0D" />
              </div>
              <div className="mt-3 text-[11px] text-[#A1A1AA] text-center">Manager can scan this QR to open the verification page.</div>
            </div>
          </div>
        )}

        {step === 6 && (
          <div>
            <h2 className="text-[22px] font-black">Review</h2>
            <div className="mt-4 space-y-3">
              {[
                { l: "Business", v: d.business },
                { l: "Address", v: d.address },
                { l: "Type", v: LOCATION_TYPES.find((t) => t.key === d.type)?.label },
                { l: "People", v: d.people },
                { l: "Hours", v: d.open247 ? "24/7" : `${d.openTime} – ${d.closeTime}` },
                { l: "Shifts", v: d.shifts },
                { l: "Current Vending", v: d.current === "yes" ? "Yes" : "No" },
                { l: "Desired", v: d.desired.join(", ") || "—" },
                { l: "Manager", v: `${d.manager} · ${d.title}` },
                { l: "Contact", v: `${d.phone} · ${d.email}` },
              ].map((r) => (
                <div key={r.l} className="sb-card p-3">
                  <div className="sb-eyebrow" style={{ fontSize: 10 }}>{r.l}</div>
                  <div className="mt-1 text-[13px]">{r.v}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Nav buttons */}
      <div className="mt-8 flex gap-3">
        <button onClick={back} className="sb-btn-secondary flex-1 text-[12px]"><ArrowLeft size={14} /> Back</button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} data-testid="wizard-next" className="sb-btn-primary flex-1 text-[12px]">Next <ArrowRight size={14} /></button>
        ) : (
          <button onClick={submit} data-testid="wizard-submit" className="sb-btn-primary flex-1 text-[12px]">
            Submit Spot <CheckCircle2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
